import { z } from 'zod';

/*
 * Contrato de validação do Certificado de Acessos — do processo arbitral E da sentença.
 *
 * Morava em `features/arbitral-detalhes/types/`, e mudou para `shared/` quando o segundo
 * consumidor apareceu (o Certificado de Acessos da Sentença, em `encerramento-processo`): os
 * dois documentos compartilham os sub-schemas de propósito, para que um campo novo em evento,
 * parte ou réplica passe a valer nos dois sem ninguém lembrar de copiar.
 *
 * A promoção também rompe um CICLO DE IMPORT real: `arbitral-detalhes` já importava
 * `encerramento-processo` (`ArbitralDetailPageContainer`), então importar o schema de volta pelo
 * barrel deixava `canalAuditoriaSchemaObj` como `undefined` na avaliação top-level — e o sintoma
 * era `Cannot read properties of undefined (reading 'extend')`, não um erro de import.
 */

// O enum é fechado de propósito: canal que este front não conhece derruba o documento inteiro no
// `.parse`, em vez de virar uma prova impressa sem régua de leitura nem rótulo. Por isso o front
// sobe antes do core quando o vocabulário cresce — foi assim com o `RCS` da régua da citação.
const canalCertificadoSchema = z.enum(['WHATSAPP', 'EMAIL', 'RCS', 'CORREIOS', 'PLATAFORMA']);
const canalAuditoriaSchema = z.enum(['WHATSAPP', 'EMAIL', 'RCS', 'CORREIOS']);

const advogadoCertificadoSchema = z.object({
  nome: z.string(),
  oab: z.string().nullable(),
});

export const parteCertificadoSchema = z.object({
  parteArbitralId: z.string(),
  nome: z.string(),
  documento: z.string().nullable(),
  tipoPessoa: z.string().nullable(),
  emails: z.array(z.string()),
  telefones: z.array(z.string()),
  papel: z.string(),
  advogados: z.array(advogadoCertificadoSchema),
});

export const comprovacaoCanalSchema = z.object({
  canal: canalCertificadoSchema,
  status: z.string().nullable(),
  suficiente: z.boolean(),
  ocorridoEm: z.string().nullable(),
});

const comprovacaoParteSchema = z.object({
  parteArbitralId: z.string(),
  nome: z.string(),
  documento: z.string().nullable(),
  // `IDENTIDADE_REJEITADA` é o veto do back para quem negou ser a parte (D15/D17). O `.parse` é
  // duro: sem o valor aqui, o certificado inteiro de quem tem um requerido assim deixa de abrir —
  // por isso este front sobe antes do core.
  situacao: z.enum(['CONFIRMADO', 'PENDENTE', 'IDENTIDADE_REJEITADA']),
  canais: z.array(comprovacaoCanalSchema),
  // `nullish` e strings abertas: o core anterior ao C37 não manda o bloco, e um status, meio,
  // motivo ou ato novo do core não pode derrubar o certificado inteiro antes do deploy desta tela.
  // O resumo da citação (D-04) é opcional pela mesma razão: o core anterior não o manda, e sem
  // ele a tela fica com o selo da comprovação.
  citacao: z
    .object({
      statusCitacao: z.string().nullable(),
      meioCitacao: z.string().nullable(),
      motivoEncerramentoFisico: z.string().nullable(),
      situacao: z.string().optional(),
      citadoEm: z.string().nullish(),
      citadoNoDia: z.string().nullish(),
      ato: z.string().nullish(),
      meioRegistrado: z.string().nullish(),
      registradoPor: z.string().nullish(),
      eventos: z.array(
        z.object({
          tipo: z.string(),
          em: z.string(),
          status: z.string().nullable(),
          meio: z.string().nullable(),
          motivo: z.string().nullable(),
          ato: z.string().nullish(),
          dataAto: z.string().nullish(),
          registradoPor: z.string().nullish(),
        }),
      ),
    })
    .nullish(),
});

// Município tolera as duas formas de fio ("chave ausente" e `null` explícito) pela mesma razão
// dos campos de cadeia abaixo: o `.parse` é duro, e coordenada fora da malha brasileira — caso
// previsto — não pode derrubar o certificado inteiro em vez de exibir só o par lat/long.
const localizacaoEventoSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
  cidade: z.string().nullish(),
  estado: z.string().nullish(),
});

const eventoCertificadoSchema = z.object({
  id: z.string(),
  evento: z.string(),
  eventoOriginal: z.string(),
  dataHora: z.string(),
  messageId: z.string().nullable(),
  ip: z.string().nullable(),
  navegador: z.string().nullable(),
  sistemaOperacional: z.string().nullable(),
  // `nullish`, não `nullable`: contra um back sem core#1475 — preview da Vercel, ambiente onde o
  // par ainda não subiu, ou rollback do backend — a chave ausente derrubaria o certificado
  // INTEIRO, e o usuário veria um erro indistinguível de falha de rede. Mesmo motivo de
  // `cadeiaIntegra` logo abaixo. O evento é reusado pela auditoria e pelos acessos, então a
  // exigência valeria para toda a trilha, não só para a coluna nova.
  localizacao: localizacaoEventoSchema.nullish(),
  hashSha256: z.string().nullable(),
  prevHash: z.string().nullable(),
  versao: z.number().nullable(),
  detalhes: z.record(z.string(), z.unknown()).nullable(),
});

const tentativaAuditoriaSchema = z.object({
  numero: z.number(),
  notificacaoId: z.string(),
  iniciadaEm: z.string(),
  resultado: z.string().nullable(),
  eventos: z.array(eventoCertificadoSchema),
});

const canalAuditoriaParteSchema = z.object({
  parteArbitralId: z.string(),
  contato: z.string().nullable(),
  tentativas: z.array(tentativaAuditoriaSchema),
});

export const canalAuditoriaSchemaObj = z.object({
  canal: canalAuditoriaSchema,
  partes: z.array(canalAuditoriaParteSchema),
});

const rastreioPostalSchema = z.object({
  evento: z.string(),
  eventoOriginal: z.string(),
  dataHora: z.string(),
  detalhe: z.string().nullable(),
});

const enderecoCertificadoSchema = z.object({
  cep: z.string().nullable(),
  logradouro: z.string().nullable(),
  numero: z.string().nullable(),
  complemento: z.string().nullable(),
  bairro: z.string().nullable(),
  cidade: z.string().nullable(),
  estado: z.string().nullable(),
});

const envioPostalSchema = z.object({
  parteArbitralId: z.string(),
  trackingCode: z.string().nullable(),
  statusCorreios: z.string(),
  enderecoEntrega: enderecoCertificadoSchema.nullable(),
  rastreio: z.array(rastreioPostalSchema),
  comprovantePostagemUrl: z.string().nullable(),
  arUrl: z.string().nullable(),
  arDisponivel: z.boolean(),
});

export const acessosParteSchema = z.object({
  parteArbitralId: z.string(),
  acessos: z.array(eventoCertificadoSchema),
});

export const replicaComunicadoSchema = z.object({
  parteArbitralId: z.string(),
  canal: canalAuditoriaSchema,
  tentativa: z.number(),
  notificacaoId: z.string(),
  envioId: z.string(),
  messageId: z.string().nullable(),
  destinatarios: z.array(z.string()),
  assunto: z.string().nullable(),
  conteudoHtml: z.string().nullable(),
  conteudoTexto: z.string().nullable(),
  templateVersaoId: z.string().nullable(),
  enviadoEm: z.string().nullable(),
});

export const respostaDaParteSchema = z.object({
  parteArbitralId: z.string(),
  eventoId: z.string(),
  messageId: z.string().nullable(),
  contato: z.string().nullable(),
  texto: z.string(),
  textoTruncado: z.boolean(),
  textoTranscrito: z.boolean(),
  recebidaEm: z.string(),
});

export const instituicaoCertificadoSchema = z.object({
  orgao: z.string(),
  cnpj: z.string(),
  leiReferencia: z.string(),
  contatoEmail: z.string(),
  contatoSite: z.string(),
  contatoWhatsapp: z.string(),
  contatoHorario: z.string(),
  assinaturaCargo: z.string(),
  assinaturaImagemUrl: z.string(),
  logoUrl: z.string(),
});

export const certificadoAcessosDtoSchema = z.object({
  certificado: z.object({
    id: z.string(),
    codigoAutenticacao: z.string().nullable(),
    urlValidacao: z.string().nullable(),
    dataEmissao: z.string(),
    hashCertificado: z.string().nullable(),
    versaoHash: z.number(),
    totalEventos: z.number(),
    // Opcionais de propósito: o `.parse` é duro e derrubaria o certificado inteiro contra um back
    // ainda sem os campos (core#1253 não está em todos os ambientes). `nullable` pelo mesmo motivo:
    // back que serializa a verificação ausente como `null` explícito não pode derrubar a tela.
    cadeiaIntegra: z.boolean().nullable().optional(),
    cadeiaQuebradaNaVersao: z.number().nullable().optional(),
  }),
  processo: z.object({
    id: z.string(),
    numeroProcesso: z.string().nullable(),
    rito: z.string().nullable(),
    valorCausa: z.number().nullable(),
    dataEntrada: z.string().nullable(),
  }),
  partes: z.object({
    requerentes: z.array(parteCertificadoSchema),
    requeridos: z.array(parteCertificadoSchema),
  }),
  comprovacao: z.array(comprovacaoParteSchema),
  auditoria: z.array(canalAuditoriaSchemaObj),
  correios: z.array(envioPostalSchema),
  acessosPlataforma: z.array(acessosParteSchema),
  replicas: z.array(replicaComunicadoSchema),
  // Opcional pelo mesmo motivo de `cadeiaIntegra`: o `.parse` é duro, e o front pode ir ao ar antes
  // do back que manda o campo.
  respostas: z.array(respostaDaParteSchema).nullish(),
  textos: z.object({
    objetivo: z.array(z.string()),
    seguranca: z.array(z.string()),
  }),
  instituicao: instituicaoCertificadoSchema,
});

/**
 * Envelope de `POST /arbitral/processos/:id/certificado-acessos/gerar-pdf`.
 *
 * A `pdfUrl` é só `string` aqui de propósito: quem decide se ela é navegável é
 * `assertUrlNavegavelSegura`, logo depois do parse — como refinement do schema, a recusa de uma
 * URL insegura viraria um erro genérico de validação e perderia a mensagem que diz o que falhou.
 */
export const solicitacaoPdfCertificadoAcessosSchema = z.object({
  data: z.discriminatedUnion('status', [
    z.object({ status: z.literal('GENERATING') }),
    z.object({ status: z.literal('READY'), pdfUrl: z.string() }),
  ]),
});
